import {
  Directive,
  HostBinding,
  HostListener,
  Input,
  NgModule,
  SimpleChanges,
} from '@angular/core';
import { CommonModule, LocationStrategy } from '@angular/common';
import {
  ActivatedRoute,
  NavigationEnd,
  Router,
  UrlTree,
} from '@angular/router';
import { Subject, Subscription } from 'rxjs';

@Directive({
  selector: 'a[routerStaticLink],area[routerStaticLink]',
})
export class RouterStaticLinkDirective {
  // href attribute when it becomes `null`.
  @HostBinding('attr.href') href: string | null = null;
  /** @internal */
  onChanges = new Subject<RouterStaticLinkDirective>();

  // the url displayed on the anchor element.
  // @HostBinding('attr.href') is used rather than @HostBinding() because it removes the
  private commands: any[] | null = null;
  private subscription: Subscription;

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private locationStrategy: LocationStrategy
  ) {
    this.subscription = router.events.subscribe((s: any) => {
      if (s instanceof NavigationEnd) {
        this.updateTargetUrlAndHref();
      }
    });
  }

  @Input()
  state: any;

  /**
   * Commands to pass to {@link Router#createUrlTree Router#createUrlTree}.
   *   - **array**: commands to pass to {@link Router#createUrlTree Router#createUrlTree}.
   *   - **string**: shorthand for array of commands with just the string, i.e. `['/route']`
   *   - **null|undefined**: Disables the link by removing the `href`
   * @see {@link Router#createUrlTree Router#createUrlTree}
   */
  @Input()
  set routerStaticLink(commands: any[] | string | null | undefined) {
    if (commands != null) {
      this.commands = Array.isArray(commands) ? commands : [commands];
    } else {
      this.commands = null;
    }
  }

  get urlTree(): UrlTree | null {
    if (this.commands === null) {
      return null;
    }
    return this.router.createUrlTree(this.commands);
  }

  /** @nodoc */
  ngOnChanges(changes: SimpleChanges): any {
    this.updateTargetUrlAndHref();
    this.onChanges.next(this);
  }

  /** @nodoc */
  ngOnDestroy(): any {
    this.subscription.unsubscribe();
  }

  /** @nodoc */
  @HostListener('click', [
    '$event.button',
    '$event.ctrlKey',
    '$event.shiftKey',
    '$event.altKey',
    '$event.metaKey',
  ])
  onClick(
    button: number,
    ctrlKey: boolean,
    shiftKey: boolean,
    altKey: boolean,
    metaKey: boolean
  ): boolean {
    if (button !== 0 || ctrlKey || shiftKey || altKey || metaKey) {
      return true;
    }

    if (this.href) {
      this.router.navigateByUrl(this.href, { state: this.state });
    }
    return false;
  }

  private updateTargetUrlAndHref(): void {
    this.href =
      this.urlTree !== null
        ? this.locationStrategy.prepareExternalUrl(
            this.router.serializeUrl(this.urlTree)
          ) + '/'
        : null;
  }
}

@NgModule({
  imports: [CommonModule],
  declarations: [RouterStaticLinkDirective],
  exports: [RouterStaticLinkDirective],
})
export class RouterStaticLinkDirectiveModule {}
